import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import $ from 'jquery';

import type { RecordData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import ClientsOverviewRowComponent from 'webapp/components/clients/overview-row';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';

import type FilterSortClientInOverview from 'webapp/models-custom/filter-sort/client-in-overview';
import { timeframeFilterId } from 'webapp/models-custom/filter-sort/client-in-overview';
import type Space from 'webapp/models-custom/space';

interface Args {
	model: Space[];
	filterSort: FilterSortClientInOverview;
	isGoals?: boolean;
	isClientMetrics?: boolean;
}

export default class ClientsOverviewComponent extends Component<Args> {
	@service('store') declare storeService: Service.Store;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('view-settings-manager')
	declare viewSettingsManagerService: Service.ViewSettingsManager;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('timeframe') declare timeframeService: Service.Timeframe;

	node: HTMLElement | null = null;

	get canSort() {
		return this.currentUserService.isAdmin && !this.currentUserService.isDemo;
	}

	// is not multiselect
	get selectedTimeframe() {
		const timeframeFilter = this.args.filterSort.findFilter(timeframeFilterId);
		if (timeframeFilter) {
			const timeframeString = timeframeFilter.selected[0] as string;
			if (timeframeString) {
				return this.timeframeService.getTimeframeFromStringForFilter(timeframeString);
			}
		}
		return undefined;
	}

	getOrderedItemIds() {
		const ids: number[] = [],
			node = this.node;

		if (node) {
			node.querySelectorAll<HTMLElement>('.client-row').forEach((element) => {
				const id = Number(element.dataset.id);
				if (id) {
					ids.push(id);
				}
			});
		}

		return ids;
	}

	updateSortOrder(orderedIds: number[]) {
		this.args.model.forEach((item) => {
			const itemId = item.numericId;
			if (itemId) {
				item.order = orderedIds.indexOf(item.numericId);
				item.applyBufferedChanges(['order']); // we won't be sending save
			}
		});
	}

	initSortable(node: HTMLElement) {
		const ct = $(node);

		if (this.canSort) {
			ct.sortable({
				handle: '.client .drag',
				forcePlaceholderSize: true,
				tolerance: 'pointer',
				items: '.client-row',
				start: (_e: JQuery.Event, ui: jQueryUIInstance) => {
					ui.item.find('.drag').attr('data-position', ui.item.index());
				},
				stop: (_e: JQuery.Event, ui: jQueryUIInstance) => {
					const handle = ui.item.find('.drag'),
						position = ui.item.index();

					let orderedIds;
					if (+handle.attr('data-position')! !== position) {
						orderedIds = this.getOrderedItemIds();
						this.requestService
							.api<RecordData.Payload>('POST', '/spaces/order', orderedIds)
							.then((reply) => {
								this.storeService.pushPayload(reply);
							})
							.catch(
								this.requestService.showAllValidationErrors.bind(
									this.requestService,
								),
							);
						this.analyticsService.event(TrackingEvent.CLIENTS_REORDER);
						this.updateSortOrder(orderedIds);
					}
				},
			});
		}
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;
		if (!this.currentUserService.isDemo) {
			this.initSortable(node);
		}
	}
	<template>
		<div {{didInsert this.setupView}}>

			<CommonListLazyLoadComponent
				@filterSort={{@filterSort}}
				@initialRowCount={{10}}
				@loadMoreRowCount={{5}}
				@animationProperty="id"
				@autoLoaded={{true}}
				@bottomDistance={{700}}
				as |visibleItems exporter|
			>

				<ul {{visibleCheckContainer}}>

					{{#each visibleItems as |item|}}

						<ClientsOverviewRowComponent
							@model={{item}}
							@canSort={{this.canSort}}
							@isClientMetrics={{@isClientMetrics}}
							@isGoals={{@isGoals}}
							@selectedTimeframe={{this.selectedTimeframe}}
						/>

					{{/each}}

				</ul>

				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</CommonListLazyLoadComponent>

			<FilterNoResultsComponent @model={{@filterSort}} />

		</div>
	</template>
}
